import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import CurrencyFormat from "react-currency-format";
import { AdminListCard } from "@components/CatalogIndex";
import type { BatchAction } from "@components/CatalogIndex";
import DataTable from "@components/DataTable";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { useDeleteProjectCreditMutation } from "@generated";
import useListProjectCredits from "../api/useListProjectCredits";
import { ProjectCredit } from "../types/ProjectCredit";
import useStyles from "../styles/useStyles";

interface Props {
  projectId: string;
  projectStatus: string;
  permission: string;
  currencyUnit: string;
  paymentCreditsMap: Record<
    string,
    { paymentSource: string; description: string; criteria: string }
  >;
}

const Show: React.FC<Props> = ({
  projectId,
  projectStatus,
  permission,
  currencyUnit,
  paymentCreditsMap,
}) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const navigate = useNavigate();
  const location = useLocation();
  const styles = useStyles();

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [errorMessages, setErrorMessages] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [deleteQueue, setDeleteQueue] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const [deleteCredit] = useDeleteProjectCreditMutation();

  const {
    projectCredits,
    projectCreditsLoading,
    projectCreditsFetching,
    pageMeta,
    refetch,
  } = useListProjectCredits({
    projectId,
    paymentCreditsMap,
    page: { number: pageNumber, size: pageSize },
  });

  useEffect(() => {
    window.history.replaceState({}, "");
    refetch();
    if (location?.state?.edit) {
      setSuccessMessage("Payment credit updated successfully!");
      setShowSuccessAlert(true);
    }
    if (location?.state?.new) {
      setSuccessMessage("Payment credit created successfully!");
      setShowSuccessAlert(true);
    }
  }, [pageNumber]);

  const handleDelete = () => {
    setDeleteQueue([...selectedIds]);
    setShowConfirmationModal(true);
  };

  const confirmDelete = () => {
    setIsDeleting(true);
    if (deleteQueue.length === 0) return;

    const promises = deleteQueue.map((id) =>
      deleteCredit({
        slug: accountSlug,
        id: Number(id),
      })
    );

    Promise.all(promises)
      .then(() => {
        setSuccessMessage(
          `Payment credit${
            deleteQueue.length > 1 ? "s" : ""
          } deleted successfully!`
        );
        setShowSuccessAlert(true);
        setIsDeleting(false);
        setShowConfirmationModal(false);
        setSelectedIds([]);
        setDeleteQueue([]);
        refetch();
      })
      .catch(() => {
        setErrorMessages(
          "One or more payment credits could not be deleted. Please try again later."
        );
        setShowFailAlert(true);
        setIsDeleting(false);
      });
  };

  const getConfirmationMessage = () => (
    <div style={{ color: "#1C2655", fontSize: "14px" }}>
      {isDeleting ? (
        <SmallSpinner />
      ) : (
        <span style={{ fontWeight: "700" }}>
          {`You are about to delete ${selectedIds.length} payment credit${
            selectedIds.length > 1 ? "s" : ""
          }.`}
        </span>
      )}
      <ul style={{ marginTop: "10px" }}>
        {selectedIds.map((id) => {
          const credit = projectCredits.find((c) => c.id === id);
          return (
            <li key={id}>
              {credit?.paymentSource}
              {credit?.description ? ` / ${credit.description}` : ""}
            </li>
          );
        })}
      </ul>
      This action cannot be undone. Do you wish to proceed?
    </div>
  );

  const total = projectCredits.reduce((sum, c) => sum + c.amount, 0);

  const columns: ColumnDef<ProjectCredit>[] = [
    {
      header: "Source/Reason",
      cell(props) {
        const credit = props.row.original;
        if (credit.id === "0") {
          return (
            <div
              className={styles.editLink}
              onClick={() => {
                navigate(`/projects/${projectId}/project_credits/new`);
              }}
            >
              {credit.paymentSource}
            </div>
          );
        }
        return (
          <div
            className={styles.editLink}
            onClick={() => {
              navigate(
                `/projects/${projectId}/project_credits/edit/${credit.id}`,
                { state: { credit } }
              );
            }}
          >
            {credit.paymentSource} <FontAwesomeIcon icon={faPencil} />
          </div>
        );
      },
    },
    {
      header: "Description",
      cell(props) {
        return <span>{props.row.original.description}</span>;
      },
    },
    {
      header: "Amount",
      cell(props) {
        if (props.row.original.id === "0") return "";
        return (
          <span className={styles.amountCell}>
            <CurrencyFormat
              displayType="text"
              prefix={currencyUnit}
              isNumericString={true}
              thousandSeparator={true}
              value={props.row.original.amount.toFixed(2)}
            />
          </span>
        );
      },
    },
    {
      header: "Criteria",
      cell(props) {
        return <span>{props.row.original.criteria}</span>;
      },
    },
  ];

  if (permission === "none") {
    return (
      <div className={styles.page}>
        You do not have permission to view this page. Please contact your
        administrator for assistance.
      </div>
    );
  }

  const canManage = projectStatus === "building";

  const batchActions: BatchAction[] = [
    {
      icon: faTrashCan,
      label: "Delete",
      onClick: handleDelete,
      variant: "danger",
    },
  ];

  return (
    <div className={styles.page}>
      {handleAlert(
        showFailAlert,
        errorMessages,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      <AdminListCard
        title="Payment Credits"
        onNew={
          canManage && (permission === "create" || permission === "manage")
            ? () =>
                navigate(`/projects/${projectId}/project_credits/new`, {
                  state: {},
                })
            : undefined
        }
        newButtonLabel="Add Credit"
        batchActions={batchActions}
        selectedCount={selectedIds.length}
      >
        {projectCreditsLoading || projectCreditsFetching ? (
          <ScopeStackSpinner />
        ) : projectCredits.length === 0 ? (
          <DataTable
            data={[
              {
                id: "0",
                amount: 0,
                criteria: "",
                paymentCreditId: null,
                paymentSource: "Add payment credits to get started",
                description: "",
              },
            ]}
            columns={columns}
            selectable={false}
            striped
            hover
          />
        ) : (
          <DataTable
            data={projectCredits}
            columns={columns}
            selectable={permission === "manage" && canManage}
            striped
            hover
            totalPages={pageMeta.pageCount}
            totalRows={pageMeta.recordCount}
            currentPage={pageNumber}
            setCurrentPage={setPageNumber}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            paginationEnabled
            pageSizeEnabled
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
          />
        )}
        {projectCredits.length > 0 && (
          <div className={styles.total}>
            <strong>Total:</strong>&nbsp;{currencyUnit}
            {total.toFixed(2)}
          </div>
        )}
        <ScopeStackModal
          modalTitle="Delete Payment Credit"
          modalBody={getConfirmationMessage()}
          handleButton2Click={isDeleting ? () => null : confirmDelete}
          handleClose={() => {
            setShowConfirmationModal(false);
            setDeleteQueue([]);
          }}
          show={showConfirmationModal}
          handleButton1Click={() => {
            setShowConfirmationModal(false);
            setDeleteQueue([]);
          }}
          deleteModal={true}
          button1Text="Cancel"
          button2Text="Delete"
        />
      </AdminListCard>
    </div>
  );
};

export default Show;
